import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import useProtecaoDeRota from '../hooks/useProtecaoDeRota';
import { Spin, message } from 'antd';
import TabelaDados from '../componentes/Table';
import useApi from '../hooks/useApi';
import ModalDelete from '../componentes/modal/DeleteDepartamento';
import ModalAdd from '../componentes/modal/AddDepartamento';
import ModalEdit from '../componentes/modal/EditarDepartamento';

const PaginaDepartamentos = () => {
  const verificando = useProtecaoDeRota();
  const [departamentos, setDepartamentos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const previousDepartamentosRef = useRef([]);  // Ref para armazenar dados anteriores

  const { callApi } = useApi('https://vision.alfama.com/api/view/departamentos', { 
    includeToken: true,
  });

  const extraColumns = useMemo(() => [
    { id: 'nome', name: 'Nome', sort: true, mwidth: '15%' },
    { id: 'categoria', name: 'Categoria', sort: true, mwidth: '5%' },
    { id: 'data_criacao', name: 'Criado em', sort: true, width: '10%' },
    { id: 'data_atualizacao', name: 'Atualizado em', sort: true, width: '10%' },
  ], []);

  const showMessage = useCallback((type, content) => {
    message[type](content);
  }, []);

  const formatarDepartamentos = useCallback((departamentos) => {
    return departamentos.map(departamento => ({
      id: departamento.departamento_id,
      nome: departamento.nome_departamento,
      categoria_id: departamento.categoria_id, // Passa o id da categoria
      categoria: departamento.nome_categoria,  // Exibe o nome da categoria
      data_criacao: departamento.departamento_data_criacao,
      data_atualizacao: departamento.departamento_data_atualizacao,
      status: departamento.situacao_departamento,
    }));
  }, []);
  

  const buscarDepartamentos = useCallback(async () => {
    try {
      const resultado = await callApi();
      const departamentosFormatados = formatarDepartamentos(resultado);

      // Verifica se os dados mudaram
      if (JSON.stringify(departamentosFormatados) !== JSON.stringify(previousDepartamentosRef.current)) {
        setDepartamentos(departamentosFormatados);
        previousDepartamentosRef.current = departamentosFormatados;  // Atualiza os dados anteriores
      }
    } catch (erro) {
      console.error('Erro ao buscar departamentos:', erro);
      showMessage('error', 'Não foi possível carregar os departamentos');
    } finally {
      setIsLoading(false);
    }
  }, [callApi, formatarDepartamentos, showMessage]);

  const handleModalOpen = useCallback((type, id) => {
    setModalType(type);
    setSelectedId(id);
    setModalVisible(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalVisible(false);
    setModalType(null);
    setSelectedId(null);
    buscarDepartamentos();  // Atualiza os departamentos ao fechar qualquer modal
  }, [buscarDepartamentos]);

  const handleDeleteConfirm = useCallback(async (success) => {
    if (success) {
      await buscarDepartamentos();
    }
    handleModalClose();
  }, [buscarDepartamentos, handleModalClose]);

  const handleEditConfirm = useCallback(async (success) => {
    if (success) {
      await buscarDepartamentos();
    }
    handleModalClose();
  }, [buscarDepartamentos, handleModalClose]);

  const handleAddConfirm = useCallback(async (success) => {
    if (success) {
      await buscarDepartamentos();
    }
    handleModalClose();
  }, [buscarDepartamentos, handleModalClose]);

  const getSelectedDepartamento = useCallback((id) => {
    return departamentos.find(dep => dep.id === id) || {};
  }, [departamentos]);

  useEffect(() => {
    if (!verificando) {
      buscarDepartamentos();
    }

    const intervalId = setInterval(() => {
      buscarDepartamentos();  // Atualiza os departamentos a cada 5 minutos
    }, 300000);  // Atualização a cada 5 minutos

    return () => {
      clearInterval(intervalId);
    };
  }, [buscarDepartamentos, verificando]);



  return (
    <>
      <TabelaDados 
        data={departamentos} 
        extraColumns={extraColumns}
        onModalOpen={handleModalOpen}
      />

      {modalType === 'delete' && (
        <ModalDelete
          visible={modalVisible}
          id={selectedId}
          onCancel={handleModalClose}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {modalType === 'edit' && (
        <ModalEdit
          visible={modalVisible}
          id={selectedId}
          initialData={getSelectedDepartamento(selectedId)}
          onCancel={handleModalClose}
          onConfirm={handleEditConfirm}
        />
      )}

      {modalType === 'add' && (
        <ModalAdd
          visible={modalVisible}
          onCancel={handleModalClose}
          onConfirm={handleAddConfirm}
        />
      )}
    </>
  );
};

export default PaginaDepartamentos;
